import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { X, RefreshCw, Copy, Check, Loader, Terminal, ArrowDown, Code, AlignLeft, Filter, Download, History, Cpu, HardDrive, Wifi, Activity, Thermometer, Monitor, Search, Server, Play } from 'lucide-react';
import * as clusterService from '../services/clusterService';
import { useAuth } from '../hooks/useAuth';

// ── Log parsing helpers ─────────────────────────────────────────────

function parseLine(raw) {
  const line = raw.trim();
  if (!line) return null;

  const skypilotMatch = line.match(/^\(task,\s*pid=\d+\)\s*(.*)$/);
  const content = skypilotMatch ? skypilotMatch[1] : line;

  // Check for ISO timestamp prefix added by start.sh (e.g. "2025-07-28T06:08:01Z message")
  const tsPrefix = content.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z)\s+(.*)$/);
  const tsTime = tsPrefix ? tsPrefix[1] : '';
  const body = tsPrefix ? tsPrefix[2] : content;
  const shortTime = tsTime ? tsTime.match(/T(\d{2}:\d{2}:\d{2})/)?.[1] || '' : '';

  if (body.startsWith('{')) {
    try {
      const parsed = JSON.parse(body);
      if (parsed.fields?.message !== undefined) {
        const msg = parsed.fields.message;
        const level = (parsed.level || 'INFO').toUpperCase();
        const target = parsed.target || '';
        const ts = parsed.timestamp || tsTime;
        let jsonTime = '';
        if (ts) {
          const m = ts.match(/T(\d{2}:\d{2}:\d{2})/);
          if (m) jsonTime = m[1];
        }
        return { type: 'json', level, message: msg, target, time: jsonTime || shortTime, raw: line };
      }
    } catch {}
  }

  if (/^[├└│─\s]*/.test(body) && (body.includes('Waiting for') || body.includes('Job started') || body.includes('Streaming logs'))) {
    return { type: 'system', level: 'SYSTEM', message: body.replace(/^[├└│─\s]+/, '').trim(), time: shortTime, raw: line };
  }

  return { type: 'plain', level: 'INFO', message: body, time: shortTime, raw: line };
}

function mergeConsecutiveLines(lines) {
  if (lines.length === 0) return lines;
  const merged = [];
  for (const line of lines) {
    const prev = merged[merged.length - 1];
    if (
      prev &&
      prev.type === 'json' && line.type === 'json' &&
      prev.time && line.time &&
      prev.time === line.time &&
      prev.level === line.level &&
      prev.target === line.target
    ) {
      prev.message += line.message;
    } else {
      merged.push({ ...line });
    }
  }
  return merged;
}

const levelColors = {
  INFO: 'text-gray-300',
  WARN: 'text-yellow-400',
  WARNING: 'text-yellow-400',
  ERROR: 'text-red-400',
  DEBUG: 'text-gray-500',
  SYSTEM: 'text-blue-400',
};

const levelBadgeColors = {
  INFO: 'bg-gray-700 text-gray-300',
  WARN: 'bg-yellow-900/50 text-yellow-400',
  WARNING: 'bg-yellow-900/50 text-yellow-400',
  ERROR: 'bg-red-900/50 text-red-400',
  DEBUG: 'bg-gray-800 text-gray-500',
  SYSTEM: 'bg-blue-900/50 text-blue-400',
};

// ── Metrics helpers ─────────────────────────────────────────────────

const formatBytes = (bytes) => {
  if (bytes === 0 || bytes == null) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(Math.abs(bytes)) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

const formatBytesPerSec = (bps) => {
  if (bps === 0 || bps == null) return '0 B/s';
  return formatBytes(bps) + '/s';
};

const ProgressBar = ({ percent, color = 'bg-blue-500', height = 'h-2', label, sublabel }) => (
  <div>
    {(label || sublabel) && (
      <div className="flex justify-between text-xs text-gray-400 mb-0.5">
        <span>{label}</span>
        <span>{sublabel}</span>
      </div>
    )}
    <div className={`w-full bg-gray-700 rounded-full ${height} overflow-hidden`}>
      <div
        className={`${color} ${height} rounded-full transition-all duration-500`}
        style={{ width: `${Math.min(Math.max(percent || 0, 0), 100)}%` }}
      />
    </div>
  </div>
);

const getBarColor = (percent) => {
  if (percent >= 90) return 'bg-red-500';
  if (percent >= 70) return 'bg-yellow-500';
  return 'bg-emerald-500';
};

const getTempColor = (temp) => {
  if (temp >= 85) return 'text-red-400';
  if (temp >= 70) return 'text-yellow-400';
  return 'text-green-400';
};

// ── Metrics helpers ─────────────────────────────────────────────────

const filterProcesses = (list) =>
  (list || []).filter(p => !p.command?.startsWith('/root/skypilot-runtime/') && !p.command?.includes('ray::IDLE'));

// ── Metrics Panel (dark theme) ──────────────────────────────────────

const MetricsPanel = ({ cluster }) => {
  const [metrics, setMetrics] = useState(null);
  const [allNodeMetrics, setAllNodeMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const intervalRef = useRef(null);

  const numNodes = cluster.num_nodes || 1;
  const isMultiNode = numNodes > 1;

  const fetchMetrics = useCallback(async () => {
    try {
      const nodeIdx = isMultiNode && selectedNode !== null ? selectedNode : undefined;
      const data = await clusterService.getClusterMetrics(
        cluster.id, nodeIdx === undefined ? undefined : { nodeIndex: nodeIdx });
      if (!data.success || !data.metrics) return;

      // The orchestrator wraps its reply as {nodes: [...], num_nodes: N}
      // whenever no node_index is requested — which includes every
      // single-node cluster. Unwrapping only in the multi-node branch left
      // `metrics` set to the envelope, so `metrics.cpu` was undefined and the
      // panel rendered "0 cores / 0%" for a perfectly healthy machine.
      const payload = data.metrics;
      let node = payload;
      if (Array.isArray(payload.nodes)) {
        setAllNodeMetrics(isMultiNode ? payload.nodes : null);
        node = payload.nodes.find((n) => !n.error) || payload.nodes[0] || {};
      } else if (!isMultiNode) {
        setAllNodeMetrics(null);
      }

      setMetrics(node);
      setLastUpdated(new Date());
      // A node that reported an error carries no counters. Say so rather than
      // drawing zeroes, which read as an idle machine.
      setError(node && node.error
        ? `${node.error}${node.detail ? ` — ${node.detail}` : ''}`
        : null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [cluster.id, isMultiNode, selectedNode]);

  useEffect(() => {
    setLoading(true);
    fetchMetrics();
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [cluster.id, selectedNode]);

  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (autoRefresh) {
      intervalRef.current = setInterval(fetchMetrics, 10000);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [autoRefresh, fetchMetrics]);

  const gpuTypeLabel = useMemo(() => {
    if (!metrics) return '';
    if (metrics.gpu_type === 'nvidia') return 'NVIDIA';
    if (metrics.gpu_type === 'amd') return 'AMD';
    if (metrics.gpu_type === 'neuron') return 'AWS Neuron';
    return '';
  }, [metrics?.gpu_type]);

  if (loading && !metrics) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader className="animate-spin text-emerald-400 mr-2" size={20} />
        <span className="text-gray-400 text-sm">Fetching metrics...</span>
      </div>
    );
  }

  if (error && !metrics) {
    return (
      <div className="p-4">
        <div className="bg-red-900/30 border border-red-800 rounded-lg p-3 text-sm text-red-400">{error}</div>
      </div>
    );
  }

  if (!metrics) return null;

  return (
    <div className="p-4 space-y-4 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 150px)' }}>
      {/* Refresh controls */}
      <div className="flex items-center justify-between text-xs text-gray-500">
        <div className="flex items-center space-x-3">
          <label className="flex items-center space-x-1.5 cursor-pointer">
            <span>Auto-refresh 10s</span>
            <button
              onClick={() => setAutoRefresh(v => !v)}
              className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors ${
                autoRefresh ? 'bg-emerald-600' : 'bg-gray-600'
              }`}
            >
              <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                autoRefresh ? 'translate-x-3.5' : 'translate-x-0.5'
              }`} />
            </button>
          </label>
          {lastUpdated && <span className="text-gray-600">{lastUpdated.toLocaleTimeString()}</span>}
        </div>
        <button
          onClick={() => { setLoading(true); fetchMetrics(); }}
          className="text-gray-400 hover:text-gray-200 p-1"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Node selector for multi-node clusters */}
      {isMultiNode && (
        <div className="flex items-center space-x-1 overflow-x-auto pb-1">
          <Server size={12} className="text-gray-500 shrink-0 mr-1" />
          <button
            onClick={() => setSelectedNode(null)}
            className={`px-2.5 py-1 text-xs rounded-md whitespace-nowrap transition-colors ${
              selectedNode === null
                ? 'bg-emerald-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:text-gray-200 border border-gray-700'
            }`}
          >
            Overview
          </button>
          {Array.from({ length: numNodes }, (_, i) => (
            <button
              key={i}
              onClick={() => setSelectedNode(i)}
              className={`px-2.5 py-1 text-xs rounded-md whitespace-nowrap transition-colors ${
                selectedNode === i
                  ? 'bg-emerald-600 text-white'
                  : 'bg-gray-800 text-gray-400 hover:text-gray-200 border border-gray-700'
              }`}
            >
              {i === 0 ? 'Head' : `Worker ${i}`}
              {allNodeMetrics?.[i] && !allNodeMetrics[i].error && (
                <span className="ml-1.5 inline-block w-1.5 h-1.5 rounded-full bg-green-400" />
              )}
              {allNodeMetrics?.[i]?.error && (
                <span className="ml-1.5 inline-block w-1.5 h-1.5 rounded-full bg-red-400" />
              )}
            </button>
          ))}
        </div>
      )}

      {/* Overview: summary cards for all nodes */}
      {isMultiNode && selectedNode === null && allNodeMetrics && (
        <div className="grid grid-cols-2 gap-2">
          {allNodeMetrics.map((node, i) => (
            <button
              key={i}
              onClick={() => setSelectedNode(i)}
              className="bg-gray-800 rounded-lg p-3 text-left hover:bg-gray-750 transition-colors border border-gray-700 hover:border-gray-600"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-gray-300">
                  {i === 0 ? 'Head Node' : `Worker ${i}`}
                </span>
                {!node.error ? (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-900/50 text-green-400">online</span>
                ) : (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-900/50 text-red-400">offline</span>
                )}
              </div>
              {!node.error ? (
                <div className="space-y-1 text-[11px]">
                  <div className="flex justify-between text-gray-400">
                    <span>CPU</span>
                    <span className="text-gray-200 font-mono">{node.cpu?.percent ?? '—'}%</span>
                  </div>
                  <div className="flex justify-between text-gray-400">
                    <span>RAM</span>
                    <span className="text-gray-200 font-mono">
                      {node.memory ? `${((node.memory.used || 0) / 1024).toFixed(1)}/${((node.memory.total || 0) / 1024).toFixed(1)} GB` : '—'}
                    </span>
                  </div>
                  {node.gpus?.length > 0 && (
                    <div className="flex justify-between text-gray-400">
                      <span>GPU</span>
                      <span className="text-gray-200 font-mono">{node.gpus[0].utilization ?? '—'}%</span>
                    </div>
                  )}
                </div>
              ) : (
                <span className="text-[11px] text-red-400">{node.error}</span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* CPU + Memory Row */}
      <div className="grid grid-cols-2 gap-4">
        {/* CPU */}
        <div className="bg-gray-800 rounded-lg p-3">
          <div className="flex items-center space-x-2 mb-2">
            <Cpu size={14} className="text-blue-400" />
            <span className="font-semibold text-xs text-gray-300">CPU ({metrics.cpu?.count || 0} cores)</span>
            <span className="ml-auto font-mono text-sm font-bold text-gray-100">{metrics.cpu?.percent ?? 0}%</span>
          </div>
          <ProgressBar percent={metrics.cpu?.percent} color={getBarColor(metrics.cpu?.percent)} height="h-2.5" />
          {metrics.cpu?.cores?.length > 0 && (
            <div className="mt-2 grid grid-cols-8 gap-0.5">
              {metrics.cpu.cores.map((pct, i) => (
                <div key={i} className="text-center">
                  <div className="w-full bg-gray-700 rounded-sm h-3 overflow-hidden">
                    <div className={`${getBarColor(pct)} h-3 transition-all duration-500`} style={{ width: `${Math.min(pct, 100)}%` }} />
                  </div>
                  <span className="text-[8px] text-gray-600">{i}</span>
                </div>
              ))}
            </div>
          )}
          {metrics.cpu_temp?.length > 0 && (
            <div className="mt-2 flex items-center space-x-2 text-xs">
              <Thermometer size={10} className="text-gray-500" />
              {metrics.cpu_temp.slice(0, 4).map((t, i) => (
                <span key={i} className={`font-mono ${getTempColor(t.temp_c)}`}>{t.temp_c}°C</span>
              ))}
            </div>
          )}
        </div>

        {/* Memory */}
        <div className="bg-gray-800 rounded-lg p-3">
          <div className="flex items-center space-x-2 mb-2">
            <Activity size={14} className="text-purple-400" />
            <span className="font-semibold text-xs text-gray-300">Memory</span>
            <span className="ml-auto font-mono text-sm font-bold text-gray-100">{metrics.memory?.percent ?? 0}%</span>
          </div>
          <ProgressBar
            percent={metrics.memory?.percent}
            color={getBarColor(metrics.memory?.percent)}
            height="h-2.5"
            label={`${((metrics.memory?.used_mb || 0) / 1024).toFixed(1)} GB used`}
            sublabel={`${((metrics.memory?.total_mb || 0) / 1024).toFixed(1)} GB total`}
          />
          {metrics.memory?.swap_total_mb > 0 && (
            <div className="mt-2">
              <ProgressBar
                percent={metrics.memory.swap_total_mb > 0 ? (metrics.memory.swap_used_mb / metrics.memory.swap_total_mb * 100) : 0}
                color="bg-orange-500"
                label="Swap"
                sublabel={`${((metrics.memory.swap_used_mb || 0) / 1024).toFixed(1)} / ${((metrics.memory.swap_total_mb || 0) / 1024).toFixed(1)} GB`}
              />
            </div>
          )}
        </div>
      </div>

      {/* GPUs */}
      {metrics.gpus?.length > 0 && (
        <div className="bg-gray-800 rounded-lg p-3">
          <div className="flex items-center space-x-2 mb-2">
            <Monitor size={14} className="text-green-400" />
            <span className="font-semibold text-xs text-gray-300">{gpuTypeLabel} GPU ({metrics.gpus.length})</span>
          </div>
          <div className="space-y-2">
            {metrics.gpus.map((gpu, i) => (
              <div key={i} className="bg-gray-900 rounded p-2.5 border border-gray-700">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-medium text-gray-300">GPU {gpu.index}: {gpu.name}</span>
                  <div className="flex items-center space-x-3 text-xs">
                    {gpu.temperature_c != null && (
                      <span className={`font-mono ${getTempColor(gpu.temperature_c)}`}>
                        <Thermometer size={9} className="inline mr-0.5" />{gpu.temperature_c}°C
                      </span>
                    )}
                    {gpu.power_draw_w != null && (
                      <span className="text-gray-500 font-mono">{gpu.power_draw_w}W{gpu.power_limit_w ? ` / ${gpu.power_limit_w}W` : ''}</span>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <ProgressBar
                    percent={gpu.utilization_percent}
                    color={getBarColor(gpu.utilization_percent)}
                    label="Compute"
                    sublabel={`${gpu.utilization_percent ?? 0}%`}
                  />
                  <ProgressBar
                    percent={gpu.memory_total_mb ? (gpu.memory_used_mb / gpu.memory_total_mb * 100) : 0}
                    color={getBarColor(gpu.memory_total_mb ? (gpu.memory_used_mb / gpu.memory_total_mb * 100) : 0)}
                    label="VRAM"
                    sublabel={`${((gpu.memory_used_mb || 0) / 1024).toFixed(1)} / ${((gpu.memory_total_mb || 0) / 1024).toFixed(1)} GB`}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Disks + Network Row */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-gray-800 rounded-lg p-3">
          <div className="flex items-center space-x-2 mb-2">
            <HardDrive size={14} className="text-amber-400" />
            <span className="font-semibold text-xs text-gray-300">Disks</span>
          </div>
          <div className="space-y-1.5">
            {(metrics.disks || []).map((disk, i) => (
              <ProgressBar
                key={i}
                percent={disk.percent}
                color={getBarColor(disk.percent)}
                label={<span className="font-mono">{disk.mount}</span>}
                sublabel={`${(disk.used_mb / 1024).toFixed(1)} / ${(disk.total_mb / 1024).toFixed(1)} GB`}
              />
            ))}
            {(!metrics.disks || metrics.disks.length === 0) && (
              <span className="text-xs text-gray-600">No disk data</span>
            )}
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-3">
          <div className="flex items-center space-x-2 mb-2">
            <Wifi size={14} className="text-cyan-400" />
            <span className="font-semibold text-xs text-gray-300">Network</span>
          </div>
          <div className="space-y-1.5">
            {(metrics.network || []).map((iface, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <span className="font-mono text-gray-500 w-14 truncate">{iface.interface}</span>
                <div className="flex space-x-3">
                  <span className="text-green-400">
                    <span className="text-gray-600 mr-1">RX</span>{formatBytesPerSec(iface.rx_bytes_sec)}
                  </span>
                  <span className="text-blue-400">
                    <span className="text-gray-600 mr-1">TX</span>{formatBytesPerSec(iface.tx_bytes_sec)}
                  </span>
                </div>
                <div className="flex space-x-2 text-gray-600 text-[10px]">
                  <span>{formatBytes(iface.rx_total_bytes)}</span>
                  <span>{formatBytes(iface.tx_total_bytes)}</span>
                </div>
              </div>
            ))}
            {(!metrics.network || metrics.network.length === 0) && (
              <span className="text-xs text-gray-600">No network data</span>
            )}
          </div>
        </div>
      </div>

      {/* Processes */}
      {metrics.processes && (
        <div className="bg-gray-800 rounded-lg p-3">
          <div className="flex items-center space-x-2 mb-2">
            <Activity size={14} className="text-indigo-400" />
            <span className="font-semibold text-xs text-gray-300">Top Processes</span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {filterProcesses(metrics.processes.top_cpu).length > 0 && (
              <div>
                <div className="text-[10px] font-medium text-gray-500 mb-1">By CPU</div>
                <div className="bg-gray-900 rounded border border-gray-700 overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-gray-500 border-b border-gray-700">
                        <th className="text-left px-2 py-1">PID</th>
                        <th className="text-right px-2 py-1">CPU%</th>
                        <th className="text-right px-2 py-1">MEM</th>
                        <th className="text-left px-2 py-1">Command</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filterProcesses(metrics.processes.top_cpu).slice(0, 10).map((p, i) => (
                        <tr key={i} className="border-t border-gray-800">
                          <td className="px-2 py-0.5 font-mono text-gray-500">{p.pid}</td>
                          <td className="px-2 py-0.5 text-right font-mono text-gray-300">{p.cpu_percent}</td>
                          <td className="px-2 py-0.5 text-right font-mono text-gray-500">{p.rss_mb}M</td>
                          <td className="px-2 py-0.5 truncate max-w-[160px] text-gray-400" title={p.command}>{p.command}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {filterProcesses(metrics.processes.top_ram).length > 0 && (
              <div>
                <div className="text-[10px] font-medium text-gray-500 mb-1">By RAM</div>
                <div className="bg-gray-900 rounded border border-gray-700 overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-gray-500 border-b border-gray-700">
                        <th className="text-left px-2 py-1">PID</th>
                        <th className="text-right px-2 py-1">MEM%</th>
                        <th className="text-right px-2 py-1">RSS</th>
                        <th className="text-left px-2 py-1">Command</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filterProcesses(metrics.processes.top_ram).slice(0, 10).map((p, i) => (
                        <tr key={i} className="border-t border-gray-800">
                          <td className="px-2 py-0.5 font-mono text-gray-500">{p.pid}</td>
                          <td className="px-2 py-0.5 text-right font-mono text-gray-300">{p.mem_percent}</td>
                          <td className="px-2 py-0.5 text-right font-mono text-gray-500">{p.rss_mb}M</td>
                          <td className="px-2 py-0.5 truncate max-w-[160px] text-gray-400" title={p.command}>{p.command}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {filterProcesses(metrics.processes.top_vram).length > 0 && (
              <div className="lg:col-span-2">
                <div className="text-[10px] font-medium text-gray-500 mb-1">By VRAM</div>
                <div className="bg-gray-900 rounded border border-gray-700 overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-gray-500 border-b border-gray-700">
                        <th className="text-left px-2 py-1">PID</th>
                        <th className="text-right px-2 py-1">VRAM</th>
                        <th className="text-left px-2 py-1">Command</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filterProcesses(metrics.processes.top_vram).slice(0, 10).map((p, i) => (
                        <tr key={i} className="border-t border-gray-800">
                          <td className="px-2 py-0.5 font-mono text-gray-500">{p.pid}</td>
                          <td className="px-2 py-0.5 text-right font-mono text-gray-300">{p.vram_mb}M</td>
                          <td className="px-2 py-0.5 truncate max-w-[400px] text-gray-400" title={p.command}>{p.command}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Console (admin) ─────────────────────────────────────────────────
//
// Runs one command at a time on a node through the orchestrator rather than
// opening a shell. SkyPilot's SSH key is per user-hash, not per cluster, so
// handing it out would grant standing access to every cluster launched
// afterwards; running the command server-side keeps it where it belongs.
// The trade is that this is not a session: no cd, no interactive programs.

const ConsolePanel = ({ cluster }) => {
  const [command, setCommand] = useState('');
  const [history, setHistory] = useState([]);
  const [running, setRunning] = useState(false);
  const [nodeIndex, setNodeIndex] = useState(0);
  const endRef = useRef(null);
  const numNodes = cluster.num_nodes || 1;

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [history]);

  const run = async (e) => {
    e?.preventDefault();
    const cmd = command.trim();
    if (!cmd || running) return;
    setRunning(true);
    setCommand('');
    try {
      const res = await clusterService.execClusterCommand(cluster.id, cmd, {
        nodeIndex: numNodes > 1 ? nodeIndex : undefined,
      });
      setHistory((h) => [...h, { cmd, ...res }]);
    } catch (err) {
      setHistory((h) => [...h, { cmd, error: err.message }]);
    } finally {
      setRunning(false);
    }
  };

  const PRESETS = [
    ['GPU', 'nvidia-smi'],
    ['Containers', 'docker ps'],
    ['Disk', 'df -h /'],
    ['TCF logs', 'tail -n 50 /tmp/cog_0.log'],
  ];

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-700 flex-wrap">
        {PRESETS.map(([label, cmd]) => (
          <button key={label} onClick={() => setCommand(cmd)}
            className="px-2 py-0.5 text-xs bg-gray-800 text-gray-300 rounded hover:bg-gray-700">
            {label}
          </button>
        ))}
        {numNodes > 1 && (
          <select value={nodeIndex} onChange={(e) => setNodeIndex(Number(e.target.value))}
            className="ml-auto bg-gray-800 text-gray-300 text-xs rounded px-2 py-0.5 border border-gray-600">
            {Array.from({ length: numNodes }, (_, i) => (
              <option key={i} value={i}>node {i}</option>
            ))}
          </select>
        )}
      </div>

      <div className="flex-1 overflow-auto p-3 font-mono text-xs space-y-3">
        {history.length === 0 && (
          <div className="text-gray-500">
            Runs a single command on the node and returns its output. Each command is
            independent — there is no shell session, so <span className="text-gray-400">cd</span> does
            not persist and interactive programs will not work.
          </div>
        )}
        {history.map((h, i) => (
          <div key={i}>
            <div className="text-emerald-400">$ {h.cmd}</div>
            {h.error
              ? <div className="text-red-400 whitespace-pre-wrap">{h.error}</div>
              : (
                <>
                  {h.stdout && <pre className="text-gray-300 whitespace-pre-wrap">{h.stdout}</pre>}
                  {h.stderr && <pre className="text-yellow-400 whitespace-pre-wrap">{h.stderr}</pre>}
                  {h.returncode !== 0 && (
                    <div className="text-red-400">exit {h.returncode}</div>
                  )}
                  {h.truncated && <div className="text-gray-500">— output truncated —</div>}
                </>
              )}
          </div>
        ))}
        {running && <div className="text-gray-500 flex items-center gap-2"><Loader size={12} className="animate-spin" /> running…</div>}
        <div ref={endRef} />
      </div>

      <form onSubmit={run} className="flex items-center gap-2 p-2 border-t border-gray-700">
        <span className="text-emerald-400 font-mono text-xs">$</span>
        <input
          value={command}
          onChange={(e) => setCommand(e.target.value)}
          placeholder="nvidia-smi"
          disabled={running}
          className="flex-1 bg-gray-900 text-gray-200 font-mono text-xs px-2 py-1 rounded border border-gray-700 focus:border-gray-500 outline-none disabled:opacity-50"
        />
        <button type="submit" disabled={running || !command.trim()}
          className="px-3 py-1 text-xs bg-emerald-700 text-white rounded hover:bg-emerald-600 disabled:opacity-40 flex items-center gap-1">
          <Play size={12} /> Run
        </button>
      </form>
    </div>
  );
};

// ── Main Modal ──────────────────────────────────────────────────────

const ClusterLogsModal = ({ cluster, onClose }) => {
  const { isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('logs');
  const [logs, setLogs] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const [jobId, setJobId] = useState(null);
  const [jobs, setJobs] = useState([]);          // [{ job_id, status }] for the switcher
  const [selectedJob, setSelectedJob] = useState(null); // null = latest job
  const [viewMode, setViewMode] = useState('parsed');
  const [levelFilter, setLevelFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [loadingAll, setLoadingAll] = useState(false);
  const [fullMode, setFullMode] = useState(false);
  const [selectedLogNode, setSelectedLogNode] = useState(null);
  const [tunnels, setTunnels] = useState([]);
  const fullModeRef = useRef(false);
  const logContainerRef = useRef(null);
  const intervalRef = useRef(null);

  const numNodes = cluster.num_nodes || 1;
  const isMultiNode = numNodes > 1;

  // Resolved frp endpoints exposed by this cluster (Gradio/ComfyUI URLs, etc.).
  useEffect(() => {
    let cancelled = false;
    clusterService.getClusterTunnels(cluster.id)
      .then(d => { if (!cancelled) setTunnels(d.tunnels || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [cluster.id]);

  const fetchLogs = useCallback(async () => {
    if (fullModeRef.current) return;
    try {
      const opts = {};
      if (selectedLogNode !== null) opts.nodeIndex = selectedLogNode;
      if (selectedJob !== null) opts.jobId = selectedJob;
      const data = await clusterService.getClusterRuntimeLogs(cluster.id, opts);
      if (fullModeRef.current) return;
      // The job list only comes back on the head/default path (not per-node).
      if (Array.isArray(data.jobs)) setJobs(data.jobs);
      if (data.success && data.logs) {
        setLogs(data.logs);
        setJobId(data.job_id);
        setError(null);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [cluster.id, selectedLogNode, selectedJob]);

  useEffect(() => {
    setLogs('');
    setLoading(true);
    fullModeRef.current = false;
    setFullMode(false);
    fetchLogs();
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(fetchLogs, 5000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [cluster.id, selectedLogNode, selectedJob]);

  const parsedLines = useMemo(() => {
    if (!logs) return [];
    const parsed = logs.split('\n').map(parseLine).filter(Boolean);
    const merged = mergeConsecutiveLines(parsed);
    return merged.filter(l => l.message && l.message.replace(/[\r\s]/g, '').length > 0);
  }, [logs]);

  const totalLineCount = parsedLines.length;

  const filteredLines = useMemo(() => {
    let lines = parsedLines;
    if (levelFilter === 'error') {
      lines = lines.filter(l => l.level === 'ERROR' || l.level === 'WARN' || l.level === 'WARNING');
    } else if (levelFilter === 'app') {
      lines = lines.filter(l => {
        if (l.type === 'json') {
          return l.target?.includes('setup') || l.target?.includes('stdout') || l.target?.includes('stderr');
        }
        return l.type === 'plain';
      });
    }
    // Apply search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      lines = lines.filter(l => l.message?.toLowerCase().includes(q) || l.raw?.toLowerCase().includes(q));
    }
    // Cap display: 1000 lines for live view, 10000 for full history
    const displayCap = fullMode ? 10000 : 1000;
    if (lines.length > displayCap) lines = lines.slice(-displayCap);
    return lines;
  }, [parsedLines, levelFilter, searchQuery, fullMode]);

  const errorCount = useMemo(() => parsedLines.filter(l => l.level === 'ERROR').length, [parsedLines]);
  const warnCount = useMemo(() => parsedLines.filter(l => l.level === 'WARN' || l.level === 'WARNING').length, [parsedLines]);

  useEffect(() => {
    if (autoScroll && logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logs, autoScroll, viewMode, levelFilter]);

  const handleScroll = () => {
    if (!logContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = logContainerRef.current;
    setAutoScroll(scrollHeight - scrollTop - clientHeight < 100);
  };

  const scrollToBottom = () => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
      setAutoScroll(true);
    }
  };

  const copyLogs = () => {
    const text = viewMode === 'raw'
      ? logs
      : filteredLines.map(l => l.time ? `[${l.time}] ${l.message}` : l.message).join('\n');
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const fetchAllLogs = async () => {
    setLoadingAll(true);
    fullModeRef.current = true;
    setFullMode(true);
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    try {
      const opts = { tail: 100000, full: true };
      if (selectedLogNode !== null) opts.nodeIndex = selectedLogNode;
      if (selectedJob !== null) opts.jobId = selectedJob;
      const data = await clusterService.getClusterRuntimeLogs(cluster.id, opts);
      if (Array.isArray(data.jobs)) setJobs(data.jobs);
      if (data.success && data.logs) {
        setLogs(data.logs);
        setJobId(data.job_id);
        setError(null);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingAll(false);
    }
  };

  const [downloading, setDownloading] = useState(false);

  const downloadLogs = async () => {
    setDownloading(true);
    try {
      // Fetch full log history for download (matching the currently viewed job)
      const dlOpts = { tail: 100000, full: true };
      if (selectedJob !== null) dlOpts.jobId = selectedJob;
      const data = await clusterService.getClusterRuntimeLogs(cluster.id, dlOpts);
      const text = (data.success && data.logs) ? data.logs : (logs || '');
      if (!text) return;
      const blob = new Blob([text], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${cluster.cluster_name}-logs-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.log`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      // Fall back to currently loaded logs
      const text = logs || '';
      if (!text) return;
      const blob = new Blob([text], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${cluster.cluster_name}-logs-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.log`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-gray-900 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gray-800 px-5 py-3 flex justify-between items-center border-b border-gray-700">
          <div className="flex items-center space-x-3">
            <Terminal size={18} className="text-green-400" />
            <div>
              <h2 className="text-sm font-bold text-gray-100">{cluster.cluster_name}</h2>
              <p className="text-xs text-gray-400">
                {activeTab === 'logs' ? `Runtime Logs${jobId != null ? ` (Job #${jobId})` : ''}` : activeTab === 'console' ? 'Console' : 'Resource Monitor'}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-1">
            {activeTab === 'logs' && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); fetchAllLogs(); }}
                  className="text-gray-400 hover:text-gray-200 transition-colors p-1.5 rounded hover:bg-gray-700"
                  title="Fetch full history"
                  disabled={loadingAll}
                >
                  <History size={14} className={loadingAll ? 'animate-spin' : ''} />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); downloadLogs(); }}
                  className="text-gray-400 hover:text-gray-200 transition-colors p-1.5 rounded hover:bg-gray-700"
                  title="Download full logs"
                  disabled={!logs || downloading}
                >
                  <Download size={14} className={downloading ? 'animate-pulse' : ''} />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); copyLogs(); }}
                  className="text-gray-400 hover:text-gray-200 transition-colors p-1.5 rounded hover:bg-gray-700"
                  title="Copy logs"
                  disabled={!logs}
                >
                  {copied ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    fullModeRef.current = false;
                    setFullMode(false);
                    setLoading(true);
                    fetchLogs();
                    // Restart live polling
                    if (intervalRef.current) clearInterval(intervalRef.current);
                    intervalRef.current = setInterval(fetchLogs, 5000);
                  }}
                  className="text-gray-400 hover:text-gray-200 transition-colors p-1.5 rounded hover:bg-gray-700"
                  title={fullMode ? 'Back to live view' : 'Refresh'}
                >
                  <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                </button>
              </>
            )}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-200 transition-colors p-1.5 rounded hover:bg-gray-700">
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Tab bar + toolbar */}
        <div className="px-4 py-2 flex items-center justify-between border-b border-gray-700" style={{ backgroundColor: '#1a1d23' }}>
          <div className="flex items-center space-x-3">
            {/* Tabs */}
            <div className="flex rounded-md overflow-hidden border border-gray-600">
              <button
                onClick={() => setActiveTab('logs')}
                className={`px-3 py-1 text-xs flex items-center space-x-1 transition-colors ${
                  activeTab === 'logs' ? 'bg-gray-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-gray-200'
                }`}
              >
                <Terminal size={12} />
                <span>Logs</span>
              </button>
              <button
                onClick={() => setActiveTab('metrics')}
                className={`px-3 py-1 text-xs flex items-center space-x-1 transition-colors ${
                  activeTab === 'metrics' ? 'bg-gray-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-gray-200'
                }`}
              >
                <Activity size={12} />
                <span>Metrics</span>
              </button>
              {isAdmin && (
                <button
                  onClick={() => setActiveTab('console')}
                  className={`px-3 py-1 text-xs flex items-center space-x-1 transition-colors ${
                    activeTab === 'console' ? 'bg-gray-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-gray-200'
                  }`}
                >
                  <Play size={12} />
                  <span>Console</span>
                </button>
              )}
            </div>

            {/* Log-specific toolbar */}
            {activeTab === 'logs' && (
              <>
                <div className="flex rounded-md overflow-hidden border border-gray-600">
                  <button
                    onClick={() => setViewMode('parsed')}
                    className={`px-2.5 py-1 text-xs flex items-center space-x-1 transition-colors ${
                      viewMode === 'parsed' ? 'bg-gray-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-gray-200'
                    }`}
                  >
                    <AlignLeft size={12} />
                    <span>Parsed</span>
                  </button>
                  <button
                    onClick={() => setViewMode('raw')}
                    className={`px-2.5 py-1 text-xs flex items-center space-x-1 transition-colors ${
                      viewMode === 'raw' ? 'bg-gray-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-gray-200'
                    }`}
                  >
                    <Code size={12} />
                    <span>Raw</span>
                  </button>
                </div>

                {viewMode === 'parsed' && (
                  <div className="flex items-center space-x-1">
                    <Filter size={12} className="text-gray-500" />
                    <select
                      value={levelFilter}
                      onChange={(e) => setLevelFilter(e.target.value)}
                      className="bg-gray-800 border border-gray-600 text-gray-300 text-xs rounded px-2 py-1 focus:outline-none focus:border-gray-500"
                    >
                      <option value="all">All levels</option>
                      <option value="app">App only</option>
                      <option value="error">Errors & Warnings</option>
                    </select>
                  </div>
                )}

                {/* Search bar */}
                <div className="flex items-center">
                  {searchOpen ? (
                    <div className="flex items-center bg-gray-800 border border-gray-600 rounded-md overflow-hidden">
                      <Search size={12} className="text-gray-500 ml-2" />
                      <input
                        autoFocus
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search logs..."
                        className="bg-transparent text-gray-300 text-xs px-2 py-1 w-40 focus:outline-none placeholder-gray-600"
                      />
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery('')}
                          className="text-gray-500 hover:text-gray-300 px-1"
                        >
                          <X size={12} />
                        </button>
                      )}
                      <button
                        onClick={() => { setSearchOpen(false); setSearchQuery(''); }}
                        className="text-gray-500 hover:text-gray-300 px-1.5 border-l border-gray-600"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setSearchOpen(true)}
                      className="p-1.5 text-gray-400 hover:text-gray-200 bg-gray-800 border border-gray-600 rounded-md"
                      title="Search logs"
                    >
                      <Search size={12} />
                    </button>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Counters (logs tab) */}
          {activeTab === 'logs' && (
            <div className="flex items-center space-x-2 text-xs">
              <span className="text-gray-500">
                {filteredLines.length}{totalLineCount > filteredLines.length ? ` / ${totalLineCount}` : ''} lines
              </span>
              {errorCount > 0 && <span className="text-red-400">{errorCount} error{errorCount !== 1 ? 's' : ''}</span>}
              {warnCount > 0 && <span className="text-yellow-400">{warnCount} warn{warnCount !== 1 ? 's' : ''}</span>}
            </div>
          )}
        </div>

        {/* Content area */}
        <div className="flex-1 relative min-h-0">
          {activeTab === 'logs' && (
            <>
              {/* Exposed endpoints (frp tunnels) */}
              {tunnels.length > 0 && (
                <div className="flex items-center flex-wrap gap-2 px-4 py-2 border-b border-gray-700"
                     style={{ backgroundColor: '#1e2128' }}>
                  <span className="text-xs text-gray-500 mr-1">Endpoints:</span>
                  {tunnels.map((t, i) => (
                    (t.url ? (
                      <a key={i} href={t.url} target="_blank" rel="noopener noreferrer"
                         className="px-2 py-1 text-xs rounded-md bg-purple-900/40 text-purple-300 hover:text-purple-100 border border-purple-800 whitespace-nowrap">
                        {t.name} ({t.protocol}) ↗
                      </a>
                    ) : (
                      <span key={i} className="px-2 py-1 text-xs rounded-md bg-gray-800 text-gray-300 border border-gray-700 whitespace-nowrap font-mono">
                        {t.name} ({t.protocol}): {t.endpoint}
                      </span>
                    ))
                  ))}
                </div>
              )}

              {/* Node selector for multi-node clusters */}
              {isMultiNode && (
                <div className="flex items-center space-x-1 px-4 py-2 border-b border-gray-700 bg-gray-850 overflow-x-auto"
                     style={{ backgroundColor: '#1e2128' }}>
                  <Server size={12} className="text-gray-500 shrink-0 mr-1" />
                  <button
                    onClick={() => setSelectedLogNode(null)}
                    className={`px-2.5 py-1 text-xs rounded-md whitespace-nowrap transition-colors ${
                      selectedLogNode === null
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-800 text-gray-400 hover:text-gray-200 border border-gray-700'
                    }`}
                  >
                    All Nodes
                  </button>
                  {Array.from({ length: numNodes }, (_, i) => (
                    <button
                      key={i}
                      onClick={() => setSelectedLogNode(i)}
                      className={`px-2.5 py-1 text-xs rounded-md whitespace-nowrap transition-colors ${
                        selectedLogNode === i
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-800 text-gray-400 hover:text-gray-200 border border-gray-700'
                      }`}
                    >
                      {i === 0 ? 'Head' : `Worker ${i}`}
                    </button>
                  ))}
                </div>
              )}

              {/* Job selector — switch between jobs that have run on this cluster.
                  Only for the head/default path (per-node logs are not job-based). */}
              {selectedLogNode === null && jobs.length > 1 && (
                <div className="flex items-center space-x-1 px-4 py-2 border-b border-gray-700 overflow-x-auto"
                     style={{ backgroundColor: '#1e2128' }}>
                  <History size={12} className="text-gray-500 shrink-0 mr-1" />
                  <button
                    onClick={() => setSelectedJob(null)}
                    title="Most recent job"
                    className={`px-2.5 py-1 text-xs rounded-md whitespace-nowrap transition-colors ${
                      selectedJob === null
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-800 text-gray-400 hover:text-gray-200 border border-gray-700'
                    }`}
                  >
                    Latest
                  </button>
                  {jobs.map((j) => {
                    const running = String(j.status || '').toUpperCase().includes('RUNNING');
                    return (
                      <button
                        key={j.job_id}
                        onClick={() => setSelectedJob(j.job_id)}
                        title={j.status || ''}
                        className={`px-2.5 py-1 text-xs rounded-md whitespace-nowrap transition-colors flex items-center gap-1 ${
                          selectedJob === j.job_id
                            ? 'bg-green-600 text-white'
                            : 'bg-gray-800 text-gray-400 hover:text-gray-200 border border-gray-700'
                        }`}
                      >
                        {running && <span className="w-1.5 h-1.5 rounded-full bg-green-400 shrink-0" />}
                        Job #{j.job_id}
                      </button>
                    );
                  })}
                </div>
              )}

              {loading && !logs && (
                <div className="flex items-center justify-center py-16">
                  <Loader className="animate-spin text-green-400 mr-2" size={20} />
                  <span className="text-gray-400 text-sm">Fetching logs...</span>
                </div>
              )}

              {error && !logs && (
                <div className="p-4">
                  <div className="bg-red-900/30 border border-red-800 rounded-lg p-3 text-sm text-red-400">{error}</div>
                </div>
              )}

              {(logs || (!loading && !error)) && (
                <div
                  ref={logContainerRef}
                  onScroll={handleScroll}
                  className="overflow-auto h-full font-mono text-xs"
                  style={{ maxHeight: 'calc(90vh - 150px)' }}
                >
                  {viewMode === 'raw' ? (
                    <pre className="text-gray-300 p-4 whitespace-pre-wrap leading-relaxed">
                      {logs || <span className="text-gray-500 italic">No logs available</span>}
                    </pre>
                  ) : (
                    <div className="divide-y divide-gray-800/50">
                      {filteredLines.length === 0 && (
                        <div className="text-gray-500 italic p-4">No logs available</div>
                      )}
                      {filteredLines.map((line, i) => (
                        <div key={i} className={`px-4 py-1 flex items-start hover:bg-gray-800/50 ${
                          line.level === 'ERROR' ? 'bg-red-950/20' : ''
                        }`}>
                          {line.time && (
                            <span className="text-gray-600 mr-3 shrink-0 tabular-nums">{line.time}</span>
                          )}
                          <span className={`inline-block w-12 shrink-0 text-center rounded text-[10px] font-medium mr-3 py-0.5 ${
                            levelBadgeColors[line.level] || levelBadgeColors.INFO
                          }`}>
                            {line.level === 'WARNING' ? 'WARN' : line.level}
                          </span>
                          <span className={`flex-1 break-all whitespace-pre-wrap ${levelColors[line.level] || 'text-gray-300'}`}>
                            {line.message}
                          </span>
                          {line.target && line.type === 'json' && (
                            <span className="text-gray-600 ml-3 shrink-0 text-[10px] max-w-[120px] truncate" title={line.target}>
                              {line.target.replace('coglet::', '')}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {!autoScroll && logs && (
                <button
                  onClick={scrollToBottom}
                  className="absolute bottom-4 right-6 bg-gray-700 hover:bg-gray-600 text-gray-300 p-2 rounded-full shadow-lg transition-colors"
                  title="Scroll to bottom"
                >
                  <ArrowDown size={14} />
                </button>
              )}
            </>
          )}

          {activeTab === 'metrics' && (
            <MetricsPanel cluster={cluster} />
          )}

          {activeTab === 'console' && isAdmin && (
            <ConsolePanel cluster={cluster} />
          )}
        </div>

        {/* Footer (logs tab only) */}
        {activeTab === 'logs' && (
          <div className="bg-gray-800 px-4 py-2 border-t border-gray-700 flex items-center justify-between text-xs text-gray-500">
            <div className="flex items-center space-x-3">
              <span>Auto-refreshing every 5s</span>
              <label className="flex items-center space-x-1.5 cursor-pointer select-none">
                <span>Auto-scroll</span>
                <button
                  onClick={() => setAutoScroll(v => !v)}
                  className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors ${
                    autoScroll ? 'bg-green-600' : 'bg-gray-600'
                  }`}
                >
                  <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                    autoScroll ? 'translate-x-3.5' : 'translate-x-0.5'
                  }`} />
                </button>
              </label>
            </div>
            {(loading || loadingAll) && (
              <span className="text-green-400 flex items-center space-x-1">
                <Loader size={10} className="animate-spin" />
                <span>{loadingAll ? 'Fetching history...' : 'Updating...'}</span>
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClusterLogsModal;
